
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ChartBarsImportInfo} from './ChartBarsImportInfo';
import {FieldModelChartBarsImportInfo} from './ChartBarsImportInfo';

/**
 * ChartBarsImportNotify struct
 */
class ChartBarsImportNotify {
  /**
   * Initialize struct
   * @param {!UUID=} sessionId
   * @param {!ChartBarsImportInfo=} importBarsInfo
   * @constructor
   */
  constructor (sessionId = new UUID(), importBarsInfo = new ChartBarsImportInfo()) {
    this.SessionId = sessionId
    this.ImportBarsInfo = importBarsInfo
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ChartBarsImportNotify}
   * @param {!ChartBarsImportNotify} other Other struct
   * @returns {!ChartBarsImportNotify} This struct
   */
  copy (other) {
    if (other.SessionId != null) {
      this.SessionId = new UUID(other.SessionId)
    } else {
      this.SessionId = null
    }
    if (other.ImportBarsInfo != null) {
      this.ImportBarsInfo = ChartBarsImportInfo.fromObject(other.ImportBarsInfo)
    } else {
      this.ImportBarsInfo = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ChartBarsImportNotify}
   * @returns {!ChartBarsImportNotify} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ChartBarsImportNotifyModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ChartBarsImportNotifyModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ChartBarsImportNotify}
   * @param {!ChartBarsImportNotify} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ChartBarsImportNotify)) {
      throw new TypeError('Instance of ChartBarsImportNotify is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ChartBarsImportNotify}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      SessionId: ((this.SessionId != null) ? this.SessionId.toString() : null),
      ImportBarsInfo: ((this.ImportBarsInfo != null) ? this.ImportBarsInfo : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ChartBarsImportNotify.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ChartBarsImportNotify} other Object value
   * @returns {!ChartBarsImportNotify} Created struct
   */
  static fromObject (other) {
    return new ChartBarsImportNotify().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ChartBarsImportNotify}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ChartBarsImportNotify.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ChartBarsImportNotify}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 3024
  }
}

export { ChartBarsImportNotify };

/**
 * Fast Binary Encoding ChartBarsImportNotify field model
 */
class FieldModelChartBarsImportNotify extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.SessionId = new fbe.FieldModelUUID(buffer, 4 + 4)
    this.ImportBarsInfo = new FieldModelChartBarsImportInfo(buffer, this.SessionId.fbeOffset + this.SessionId.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelChartBarsImportNotify}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelChartBarsImportNotify}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.SessionId.fbeSize + this.ImportBarsInfo.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelChartBarsImportNotify}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.SessionId.fbeExtra + this.ImportBarsInfo.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelChartBarsImportNotify}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelChartBarsImportNotify.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelChartBarsImportNotify}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 3024
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelChartBarsImportNotify}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelChartBarsImportNotify}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.SessionId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SessionId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SessionId.fbeSize

    if ((fbeCurrentSize + this.ImportBarsInfo.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ImportBarsInfo.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ImportBarsInfo.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelChartBarsImportNotify}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelChartBarsImportNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelChartBarsImportNotify}
   * @param {!ChartBarsImportNotify} fbeValue Default value, defaults is new ChartBarsImportNotify()
   * @returns {!ChartBarsImportNotify} ChartBarsImportNotify value
   */
  get (fbeValue = new ChartBarsImportNotify()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelChartBarsImportNotify}
   * @param {!ChartBarsImportNotify} fbeValue ChartBarsImportNotify value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.SessionId.fbeSize) <= fbeStructSize) {
      fbeValue.SessionId = this.SessionId.get()
    } else {
      fbeValue.SessionId = new UUID()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SessionId.fbeSize

    if ((fbeCurrentSize + this.ImportBarsInfo.fbeSize) <= fbeStructSize) {
      fbeValue.ImportBarsInfo = this.ImportBarsInfo.get()
    } else {
      fbeValue.ImportBarsInfo = new ChartBarsImportInfo()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ImportBarsInfo.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelChartBarsImportNotify}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelChartBarsImportNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelChartBarsImportNotify}
   * @param {!ChartBarsImportNotify} fbeValue ChartBarsImportNotify value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelChartBarsImportNotify}
   * @param {ChartBarsImportNotify} fbeValue ChartBarsImportNotify value
   */
  setFields (fbeValue) {
    this.SessionId.set(fbeValue.SessionId)
    this.ImportBarsInfo.set(fbeValue.ImportBarsInfo)
  }
}

export { FieldModelChartBarsImportNotify };

/**
 * Fast Binary Encoding ChartBarsImportNotify model
 */
class ChartBarsImportNotifyModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelChartBarsImportNotify(this.buffer, 4)
  }

  /**
   * Get the ChartBarsImportNotify model
   * @this {!ChartBarsImportNotifyModel}
   * @returns {!FieldModelChartBarsImportNotify} model ChartBarsImportNotify model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ChartBarsImportNotifyModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ChartBarsImportNotifyModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ChartBarsImportNotifyModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ChartBarsImportNotifyModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelChartBarsImportNotify.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ChartBarsImportNotifyModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ChartBarsImportNotifyModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ChartBarsImportNotifyModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ChartBarsImportNotifyModel}
   * @param {!ChartBarsImportNotify} value ChartBarsImportNotify value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ChartBarsImportNotifyModel}
   * @param {!ChartBarsImportNotify} value ChartBarsImportNotify value, defaults is new ChartBarsImportNotify()
   * @return {!object} Deserialized ChartBarsImportNotify value and its size
   */
  deserialize (value = new ChartBarsImportNotify()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ChartBarsImportNotify(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ChartBarsImportNotify(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ChartBarsImportNotifyModel}
   * @param {!number} prev Previous ChartBarsImportNotify model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ChartBarsImportNotifyModel };
